/* eslint-disable max-len */
import React, {useEffect} from 'react';
import styled, {css} from 'styled-components';
import InputRange from 'react-input-range';
import {PlayerEvent} from 'bitmovin-player';
import {colors, mobileMediaQuery, webMediaQuery} from '../../styles';
import {formattedTime} from '../../utils/time';
import VolumeUI from './VolumeUI';
import PlayerButton from './PlayerButton';
import Loader from '../Loader';
import {useSelector} from 'react-redux';
import {selectPlayerVolume} from '../../store/selectors/player';
import Settings from './Settings';
import EndScreen from './EndScreen';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom/cjs/react-router-dom.min';

var timeout = null;

export default function PlayerUI({
    player,
    isLive,
    hideFullScreen,
    startMuted,
    onRestart,
    hasNext,
    onComplete,
}) {
    const [show, setShow] = React.useState(false);
    const [value, setValue] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [isReady, setIsReady] = React.useState(false);
    const [isStalled, setIsStalled] = React.useState(false);
    const [isPlayingAd, setIsPlayingAd] = React.useState(false);
    //show extra volume UI, even when mouse is not over
    const [showVolume, setShowVolume] = React.useState(startMuted);
    const desiredVolume = useSelector(selectPlayerVolume) || 100;
    const [showSettings, setShowSettings] = React.useState(false);
    const [ended, setEnded] = React.useState(false);

    const stallTimeout = React.useRef(null);
    const playingAdRef = React.useRef(false);
    const history = useHistory();
    const {t} = useTranslation();

    const showUI = () => {
        setShow(true);

        initTimeout();
    };

    const hideUI = () => {
        setShow(false);
        clearTimeout(timeout);
        setShowSettings(false);
    };

    const initTimeout = () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            hideUI();
        }, 3000);
    };

    const onChangeValue = value => {
        player.seek(value);
        setValue(value);
    };

    const togglePlayPause = event => {
        if (player.isPlaying()) {
            player.pause();
        } else {
            player.play();
            //when player is stalled, if you pause and play, the playing event is not called correctly,
            // so we set it manually here
            stallTimeout.current = setTimeout(() => {
                setIsStalled(true);
            }, 1000);
        }
        event.stopPropagation();
    };

    useEffect(() => {
        if (!player) {
            return;
        }

        player.on(PlayerEvent.TimeChanged, () => {
            if (stallTimeout.current) {
                clearTimeout(stallTimeout.current);
                stallTimeout.current = null;
            }
            setIsStalled(false);

            //if we are waiting ad, we will not update this
            if (playingAdRef.current) {
                return;
            }

            if (!isLive) {
                try {
                    setValue(player.getCurrentTime());
                } catch (e) {
                    //player alrady destroyed, this may happen once
                }
            }
        });
        player.on(PlayerEvent.Ready, data => {
            setDuration(player.getDuration());
        });
        player.on(PlayerEvent.Paused, () => {
            setIsPlaying(false);
            setIsStalled(false);
            setIsReady(true);
        });
        player.on(PlayerEvent.Playing, () => {
            setIsPlaying(true);
            setIsReady(true);
        });
        player.on(PlayerEvent.StallStarted, () => {
            setIsStalled(true);
        });
        player.on(PlayerEvent.StallEnded, () => {
            setIsStalled(false);
        });
        player.on(PlayerEvent.AdStarted, () => {
            setIsPlayingAd(true);
        });
        player.on(PlayerEvent.AdSkipped, () => {
            setIsPlayingAd(false);
        });
        player.on(PlayerEvent.AdFinished, () => {
            setIsPlayingAd(false);
        });
        player.on(PlayerEvent.Seek, event => {
            setIsStalled(true);
            //update current time
            setValue(event.seekTarget);
        });
        player.on(PlayerEvent.Seeked, () => {
            setIsStalled(false);
        });
        player.on(PlayerEvent.VolumeChanged, event => {
            if (event.targetVolume > 0) {
                //user unmuted, can hide extra UI
                setShowVolume(false);
            }
        });
        player.on(PlayerEvent.PlaybackFinished, () => {
            setIsPlaying(false);
            setEnded(true);
        });

        return () => {
            try {
                player.off(PlayerEvent.TimeChanged);
                player.off(PlayerEvent.Ready);
                player.off(PlayerEvent.Paused);
                player.off(PlayerEvent.Playing);
                player.off(PlayerEvent.StallStarted);
                player.off(PlayerEvent.StallEnded);
                player.off(PlayerEvent.AdStarted);
                player.off(PlayerEvent.AdSkipped);
                player.off(PlayerEvent.AdFinished);
                player.off(PlayerEvent.Seek);
                player.off(PlayerEvent.Seeked);
                player.off(PlayerEvent.VolumeChanged);
                player.off(PlayerEvent.PlaybackFinished);
            } catch (e) {
                //player already destroyed, may happen during unmount
            }
            if (stallTimeout.current) {
                clearTimeout(stallTimeout.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [player]);

    useEffect(() => {
        playingAdRef.current = isPlayingAd;
    }, [isPlayingAd]);

    const onRewind = event => {
        player.seek(player.getCurrentTime() - 10);
        event.stopPropagation();
    };

    const onForward = event => {
        player.seek(player.getCurrentTime() + 10);
        event.stopPropagation();
    };

    const toggleFullScreen = event => {
        if (player.getViewMode() === 'fullscreen') {
            player.setViewMode('inline');
        } else {
            player.setViewMode('fullscreen');
        }
        event.stopPropagation();
    };
    const enableSound = () => {
        setShowVolume(false);
        player.unmute();
        player.setVolume(desiredVolume);
    };

    const _onRestart = () => {
        setEnded(false);
        onRestart();
    };

    const closeLive = () => {
        history.goBack();
    };

    useEffect(() => {
        return () => {
            clearTimeout(timeout);
        };
    }, []);

    const toggleSettings = () => {
        setShowSettings(!showSettings);
    };

    if (ended) {
        return (
            <EndScreen
                onClick={_onRestart}
                showProgress={hasNext}
                onComplete={onComplete}
            />
        );
    }

    return (
        <>
            {(isStalled || !isReady) && <StyledLoader hideProgress />}

            {!isPlayingAd && (
                <Container
                    onMouseOver={showUI}
                    onMouseLeave={hideUI}
                    onMouseMove={showUI}
                    show={show}
                >
                    <Overlay onClick={togglePlayPause} />
                    <ButtonContainer>
                        <ButtonGroup>
                            <VolumeUI
                                player={player}
                                opacity={showVolume ? 0 : 0.65}
                                startMuted={startMuted}
                            />
                            <PlayerButton
                                image={require('../../assets/playerui/ic_settings.png')}
                                onClick={toggleSettings}
                            />
                        </ButtonGroup>

                        {!isStalled && !isLive && (
                            <ButtonGroup align="center">
                                {isReady && !isLive && (
                                    <PlayerButton
                                        image={require('../../assets/playerui/ic_rewind.png')}
                                        onClick={onRewind}
                                    />
                                )}
                                <PlayerButton
                                    image={
                                        isPlaying
                                            ? require('../../assets/playerui/ic_pause.png')
                                            : require('../../assets/playerui/ic_play.png')
                                    }
                                    onClick={togglePlayPause}
                                />
                                {isReady && !isLive && (
                                    <PlayerButton
                                        image={require('../../assets/playerui/ic_forward.png')}
                                        onClick={onForward}
                                    />
                                )}
                            </ButtonGroup>
                        )}
                        <ButtonGroup align="flex-end">
                            {!hideFullScreen && (
                                <PlayerButton
                                    image={require('../../assets/playerui/ic_fullscreen.png')}
                                    onClick={toggleFullScreen}
                                />
                            )}
                        </ButtonGroup>
                    </ButtonContainer>
                    {isReady && !isLive && (
                        <ProgressBarContainer
                            onClick={event => event.stopPropagation()}
                        >
                            <TimeDisplay>{formattedTime(value)}</TimeDisplay>
                            <InputRange
                                minValue={0}
                                maxValue={duration || 1}
                                step={1}
                                value={value}
                                onChange={onChangeValue}
                                formatLabel={value => formattedTime(value)}
                            />
                        </ProgressBarContainer>
                    )}
                    {isLive && (
                        <>
                            <Center>
                                <PlayerButton
                                    image={
                                        isPlaying
                                            ? require('../../assets/playerui/ic_pause.png')
                                            : require('../../assets/playerui/ic_play.png')
                                    }
                                    onClick={togglePlayPause}
                                />
                            </Center>
                            <CloseContainer>
                                <PlayerButton
                                    image={require('../../assets/playerui/ic_close.png')}
                                    onClick={closeLive}
                                />
                            </CloseContainer>
                        </>
                    )}
                    {showSettings && (
                        <Settings player={player} isLive={isLive} />
                    )}
                </Container>
            )}
            {!isPlaying && isPlayingAd && (
                <Container show={true}>
                    <ButtonGroup align="center">
                        <PlayerButton
                            image={
                                isPlaying
                                    ? require('../../assets/playerui/ic_pause.png')
                                    : require('../../assets/playerui/ic_play.png')
                            }
                            onClick={togglePlayPause}
                        />
                    </ButtonGroup>
                </Container>
            )}
            {isLive && !isPlayingAd && (
                <LiveIndicator>{t('onnow.title')}</LiveIndicator>
            )}
            {//must show volume UI if muted to make it clear there is no sound at start, even during ads
            showVolume && (
                <SmallContainer show={true}>
                    <ButtonContainer>
                        <VolumeUI
                            player={player}
                            startMuted={startMuted}
                            enableSound={enableSound}
                        />
                    </ButtonContainer>
                </SmallContainer>
            )}
        </>
    );
}

const Container = styled.div`
    position: absolute;
    display: flex;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: ${({show}) => (show ? '1' : '0')};
    transition: 0.5s;
    flex-direction: column-reverse;
    z-index: 1000;

    ${({show}) => !show && 'cursor: none;'}

    .input-range__slider {
        appearance: none;
        background: white;
        border: 1px solid white;
        border-radius: 100%;
        cursor: pointer;
        display: block;
        height: 1rem;
        margin-left: -0.5rem;
        margin-top: -0.65rem;
        outline: none;
        position: absolute;
        top: 50%;
        transition: transform 0.3s ease-out, box-shadow 0.3s ease-out;
        width: 1rem;
    }
    .input-range__slider:active {
        transform: scale(1.3);
    }
    .input-range__slider:focus {
        box-shadow: 0 0 0 5px rgba(63, 81, 181, 0.2);
    }
    .input-range--disabled .input-range__slider {
        background: #cccccc;
        border: 1px solid #cccccc;
        box-shadow: none;
        transform: none;
    }

    .input-range__slider-container {
        transition: left 0.3s ease-out;
    }

    .input-range__label {
        transform: translateZ(0);
        white-space: nowrap;
        font-size: 14px;
        font-weight: 700;
        color: white;
    }

    .input-range__label--min {
        display: none;
    }
    .input-range__label--max {
        width: 60px;
        display: flex;
        justify-content: center;
    }

    .input-range__label--min {
        left: 0;
        margin-right: 0.5rem;
    }

    .input-range__label--max {
        right: 0;
    }

    .input-range__label--value {
        position: absolute;
        top: -1.8rem;
        display: none;
    }

    .input-range__label-container {
        position: relative;
    }
    .input-range__label--max .input-range__label-container {
    }

    .input-range__track {
        background: #eeeeee;
        border-radius: 0.3rem;
        cursor: pointer;
        display: block;
        height: 0.3rem;
        position: relative;
        transition: left 0.3s ease-out, width 0.3s ease-out;
    }
    .input-range--disabled .input-range__track {
        background: #eeeeee;
    }

    .input-range__track--background {
        left: 0;
        margin-top: -0.15rem;
        right: 0;
        top: 50%;
        flex: 1;
    }

    .input-range__track--active {
        background: ${colors.pink};
    }

    .input-range {
        height: 1rem;
        position: relative;
        width: 100%;
        display: flex;
        flex-direction: row;
    }
`;

const SmallContainer = styled(Container)`
    top: unset;
    bottom: 0;
    width: unset;
    height: unset;
`;

const Overlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.79) 0%,
        rgba(0, 0, 0, 0) 100%
    );
`;

const ProgressBarContainer = styled.div`
    display: flex;
    flex-direction: row;
    margin: 0 25px;
    align-items: center;
    z-index: 1;
    margin-bottom: 40px;
`;

const TimeDisplay = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: white;
    margin-right: 0.5rem;
    width: 60px;
    display: flex;
    justify-content: center;
    z-index: 1;
`;

const ButtonContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: 18px 25px 25px 25px;

    ${mobileMediaQuery} {
        margin: 9px 12px 12px 12px;
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    position: absolute;
    bottom: 0;
    ${({align}) =>
        align === 'center'
            ? 'left: 50%; transform: translateX(-50%);'
            : align === 'flex-end'
            ? 'right: 0;'
            : 'left: 0;'}

    ${mobileMediaQuery} {
        ${({align}) =>
            align === 'center' &&
            css`
                bottom: 80px;
            `}
`;

const StyledLoader = styled(Loader)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const LiveIndicator = styled.div`
    font-family: AvenirNextRoundedStd, Helvetica, Arial, sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: white;
    background: ${colors.pink};
    padding: 4px 12px !important;
    position: absolute;
    top: 25px;
    left: 25px;
    text-transform: uppercase;

    ${mobileMediaQuery} {
        top: 12px;
        left: 12px;
    }

    ${webMediaQuery} {
        font-size: 16px;
        padding: 6px 12px !important;
    }
`;

const Center = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const CloseContainer = styled.div`
    position: absolute;
    top: 25px;
    right: 25px;
`;
